import { getErrorStatus } from '@/api/client';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  MessageSquare,
  Pencil,
  ArrowUpRight,
  Type,
  Undo2,
  Check,
  Eraser,
  Move,
} from 'lucide-react';
import { PhotoCommentsPanel } from './PhotoCommentsPanel';
import { AnnotationCanvas } from './AnnotationCanvas';
import type { AnnotationCanvasHandle } from './AnnotationCanvas';
import { photosApi } from '@/api/photos.api';
import type {
  PhotoAnnotation,
  AnnotationType,
  PenData,
  ArrowData,
  TextData,
} from '@/api/photos.api';
import { useToast } from '@/contexts/ToastContext';

// TODO(color): annotation-picker swatches. These become user drawing data,
// not app chrome — retinting changes the available pen colours. Confirm intent
// before mapping to tokens.

interface ImageViewerModalProps {
  images: { url: string; label?: string; photoId?: string }[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.3;

const COLORS = [
  '#00A88E',
  '#FF4444',
  '#FFB800',
  '#4488FF',
  '#FF44FF',
  '#FFFFFF',
];

const TOOLS: { type: AnnotationType; icon: typeof Pencil; label: string }[] = [
  { type: 'pen', icon: Pencil, label: 'screens.imageViewerModal.tool.pen' },
  {
    type: 'arrow',
    icon: ArrowUpRight,
    label: 'screens.imageViewerModal.tool.arrow',
  },
  { type: 'text', icon: Type, label: 'screens.imageViewerModal.tool.text' },
  {
    type: 'eraser',
    icon: Eraser,
    label: 'screens.imageViewerModal.tool.erase',
  },
  { type: 'move', icon: Move, label: 'screens.imageViewerModal.tool.move' },
];

export function ImageViewerModal({
  images,
  initialIndex = 0,
  isOpen,
  onClose,
}: ImageViewerModalProps) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [commentCount, setCommentCount] = useState(0);

  // Annotation state
  const [drawMode, setDrawMode] = useState(false);
  const [activeTool, setActiveTool] = useState<AnnotationType>('pen');
  const [activeColor, setActiveColor] = useState('#00A88E');
  // Annotations are kept WITH the photo they belong to and read only when
  // that photo is the one on screen: nothing to clear when the image changes,
  // and a slow response for the previous image cannot land on this one.
  const [loaded, setLoaded] = useState<{
    photoId?: string;
    items: PhotoAnnotation[];
  }>({ items: [] });
  const canvasRef = useRef<AnnotationCanvasHandle>(null);
  const [imageNaturalSize, setImageNaturalSize] = useState({
    w: 1000,
    h: 1000,
  });
  const { showError } = useToast();

  const dragStart = useRef<{
    x: number;
    y: number;
    panX: number;
    panY: number;
  } | null>(null);

  const currentPhotoId = images[currentIndex]?.photoId;

  const annotations = useMemo(
    () => (loaded.photoId === currentPhotoId ? loaded.items : []),
    [loaded, currentPhotoId],
  );
  const setAnnotations = useCallback(
    (
      next:
        | PhotoAnnotation[]
        | ((prev: PhotoAnnotation[]) => PhotoAnnotation[]),
    ) =>
      setLoaded((prev) => {
        const current = prev.photoId === currentPhotoId ? prev.items : [];
        return {
          photoId: currentPhotoId,
          items: typeof next === 'function' ? next(current) : next,
        };
      }),
    [currentPhotoId],
  );

  // Load annotations when image changes
  useEffect(() => {
    if (!currentPhotoId) return;
    photosApi
      .getAnnotations(currentPhotoId)
      .then((items) => setLoaded({ photoId: currentPhotoId, items }))
      .catch(() => {});
  }, [currentPhotoId]);

  // Start from the requested image, unzoomed, each time the viewer opens
  // (or is pointed at another image while open). Adjusted during render.
  const [openedAt, setOpenedAt] = useState<number | null>(null);
  if (isOpen && openedAt !== initialIndex) {
    setOpenedAt(initialIndex);
    setCurrentIndex(initialIndex);
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
    setDrawMode(false);
  }
  if (!isOpen && openedAt !== null) setOpenedAt(null);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const resetTransform = useCallback(() => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  }, []);

  const goTo = useCallback(
    (index: number) => {
      setCurrentIndex(index);
      resetTransform();
      setDrawMode(false);
    },
    [resetTransform],
  );

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;
      if (drawMode) {
        if (e.key === 'Escape') setDrawMode(false);
        return;
      }
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) goTo(currentIndex - 1);
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1)
        goTo(currentIndex + 1);
      if (e.key === 'r' || e.key === 'R') setRotation((r) => (r + 90) % 360);
      if (e.key === '+' || e.key === '=')
        setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP));
      if (e.key === '-') setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP));
      if (e.key === '0') resetTransform();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    isOpen,
    currentIndex,
    images.length,
    goTo,
    onClose,
    resetTransform,
    drawMode,
  ]);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (drawMode) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z + delta)));
    },
    [drawMode],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (drawMode || e.button !== 0) return;
      e.preventDefault();
      setIsDragging(true);
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        panX: pan.x,
        panY: pan.y,
      };
    },
    [pan, drawMode],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !dragStart.current) return;
      setPan({
        x: dragStart.current.panX + (e.clientX - dragStart.current.x),
        y: dragStart.current.panY + (e.clientY - dragStart.current.y),
      });
    },
    [isDragging],
  );

  const stopDrag = useCallback(() => {
    setIsDragging(false);
    dragStart.current = null;
  }, []);

  const handleAnnotationComplete = async (
    type: AnnotationType,
    data: PenData | ArrowData | TextData,
  ) => {
    if (!currentPhotoId) return;
    try {
      const saved = await photosApi.addAnnotation(
        currentPhotoId,
        type,
        data,
        activeColor,
      );
      setAnnotations((prev) => [...prev, saved]);
    } catch {
      showError(t('screens.imageViewerModal.failedToSaveAnnotation'));
    }
  };

  const handleMoveAnnotation = async (
    id: string,
    data: PenData | ArrowData | TextData,
  ) => {
    // Optimistic update
    setAnnotations((prev) =>
      prev.map((a) => (a.id === id ? { ...a, data } : a)),
    );
    try {
      await photosApi.updateAnnotation(id, data);
    } catch {
      showError(t('screens.imageViewerModal.failedToSaveMoveReverted'));
      // Re-fetch to revert
      if (currentPhotoId) {
        photosApi
          .getAnnotations(currentPhotoId)
          .then(setAnnotations)
          .catch(() => {});
      }
    }
  };

  const handleDeleteAnnotation = async (id: string) => {
    // Optimistic update — remove immediately
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    try {
      await photosApi.deleteAnnotation(id);
    } catch (err) {
      // 404 = already deleted on server, silent success
      if (getErrorStatus(err) !== 404) {
        showError(t('screens.imageViewerModal.failedToDeleteAnnotation'));
      }
    }
  };

  if (!isOpen || images.length === 0) return null;

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;
  const isTransformed =
    zoom !== 1 || rotation !== 0 || pan.x !== 0 || pan.y !== 0;
  const current = images[currentIndex];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex bg-black"
      style={{
        margin: 0,
        padding: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
      }}
    >
      {/* Main viewer */}
      <div className="flex flex-col flex-1 min-w-0 h-full overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3 bg-neutral-800 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            {current.label && (
              <span className="text-white font-medium text-sm">
                {current.label}
              </span>
            )}
            {images.length > 1 && (
              <span className="text-white/50 text-xs">
                {currentIndex + 1} / {images.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!drawMode && (
              <span className="text-white/30 text-xs mr-3 hidden sm:block">
                {t('screens.imageViewerModal.navigateScrollZoomRRotate')}
              </span>
            )}
            {currentPhotoId && (
              <button
                onClick={() => setDrawMode((v) => !v)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors mr-1 ${
                  drawMode
                    ? 'bg-amber-500 text-white'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
                title={drawMode ? 'Exit draw mode (Esc)' : 'Annotate image'}
              >
                <Pencil className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">
                  {drawMode ? 'Drawing' : 'Annotate'}
                </span>
              </button>
            )}
            {currentPhotoId && (
              <button
                onClick={() => setShowComments((v) => !v)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors mr-1 ${
                  showComments
                    ? 'bg-primary-600 text-white'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
                title={t('screens.imageViewerModal.toggleComments')}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">
                  {t('screens.imageViewerModal.comments')}
                </span>
                {!showComments && commentCount > 0 && (
                  <span className="bg-primary-600 text-white rounded-full px-1.5 py-0.5 text-caption leading-none">
                    {commentCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Draw mode toolbar */}
        {drawMode && (
          <div className="flex items-center gap-3 px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 flex-shrink-0 flex-wrap">
            <div className="flex items-center gap-1">
              {TOOLS.map(({ type, icon: Icon, label }) => (
                <button
                  key={type}
                  aria-label={t(label)}
                  onClick={() => setActiveTool(type)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    activeTool === type
                      ? 'bg-amber-500 text-white'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t(label)}</span>
                </button>
              ))}
            </div>
            <div className="w-px h-5 bg-white/20" />
            <div className="flex items-center gap-1.5">
              {COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveColor(c)}
                  className={`w-5 h-5 rounded-full border-2 transition-all ${
                    activeColor === c
                      ? 'border-white scale-125'
                      : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div className="w-px h-5 bg-white/20" />
            <button
              onClick={() => {
                const last = annotations[annotations.length - 1];
                if (last) handleDeleteAnnotation(last.id);
              }}
              disabled={annotations.length === 0}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 transition-colors"
            >
              <Undo2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">
                {t('screens.imageViewerModal.undo')}
              </span>
            </button>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-amber-400/70 text-xs hidden md:block">
                {t('screens.imageViewerModal.rightClickAnnotationToDelete')}
              </span>
              <button
                onClick={() => setDrawMode(false)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-amber-500 hover:bg-amber-600 text-white transition-colors"
              >
                <Check className="w-3.5 h-3.5" />
                <span>{t('common.done')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Image area */}
        <div
          className="flex-1 relative overflow-hidden"
          style={{
            cursor: drawMode ? 'default' : isDragging ? 'grabbing' : 'grab',
          }}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
        >
          {/* Image + annotation canvas — transform together */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ pointerEvents: 'none', margin: 0, padding: 0 }}
          >
            <div
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transition: isDragging ? 'none' : 'transform 0.12s ease',
                transformOrigin: 'center center',
                position: 'relative',
                display: 'inline-flex',
                maxWidth: '100%',
                maxHeight: '100%',
                margin: 0,
              }}
            >
              <img
                key={currentIndex}
                src={current.url}
                alt={current.label ?? `Image ${currentIndex + 1}`}
                draggable={false}
                className="block select-none max-w-full max-h-full object-contain"
                style={{ margin: 0, padding: 0 }}
                onLoad={(e) => {
                  const img = e.currentTarget;
                  setImageNaturalSize({
                    w: img.naturalWidth,
                    h: img.naturalHeight,
                  });
                }}
              />
              {currentPhotoId && (
                <AnnotationCanvas
                  ref={canvasRef}
                  width={imageNaturalSize.w}
                  height={imageNaturalSize.h}
                  tool={activeTool}
                  color={activeColor}
                  drawMode={drawMode}
                  savedAnnotations={annotations}
                  onAnnotationComplete={handleAnnotationComplete}
                  onDeleteAnnotation={handleDeleteAnnotation}
                  onMoveAnnotation={handleMoveAnnotation}
                />
              )}
            </div>
          </div>

          {/* Prev / Next */}
          {hasPrev && !drawMode && (
            <button
              onClick={() => goTo(currentIndex - 1)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/80 border border-white/10 flex items-center justify-center transition-all z-10"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
          )}
          {hasNext && !drawMode && (
            <button
              onClick={() => goTo(currentIndex + 1)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/80 border border-white/10 flex items-center justify-center transition-all z-10"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          )}

          {/* Zoom / rotation badges */}
          {zoom !== 1 && (
            <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/70 rounded-full z-10">
              <span className="text-white text-xs font-medium">
                {Math.round(zoom * 100)}%
              </span>
            </div>
          )}
          {rotation !== 0 && (
            <div
              className="absolute top-3 px-2.5 py-1 bg-black/70 rounded-full z-10"
              style={{ left: zoom !== 1 ? 70 : 12 }}
            >
              <span className="text-white text-xs font-medium">
                {rotation}°
              </span>
            </div>
          )}
        </div>

        {/* Controls toolbar */}
        {!drawMode && (
          <div className="flex items-center justify-between px-4 py-2.5 bg-neutral-800 border-t border-white/10 flex-shrink-0">
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))
                }
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                title={t('screens.imageViewerModal.zoomOut')}
              >
                <ZoomOut className="w-4 h-4 text-white" />
              </button>
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-28 h-1 accent-primary-600 cursor-pointer"
              />
              <button
                onClick={() =>
                  setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))
                }
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                title={t('screens.imageViewerModal.zoomInTitle')}
              >
                <ZoomIn className="w-4 h-4 text-white" />
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                title={t('screens.imageViewerModal.rotateLeft')}
              >
                <RotateCw className="w-4 h-4 text-white scale-x-[-1]" />
              </button>
              <button
                onClick={() => setRotation((r) => (r + 90) % 360)}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                title={t('screens.imageViewerModal.rotateRight')}
              >
                <RotateCw className="w-4 h-4 text-white" />
              </button>
              {isTransformed && (
                <button
                  onClick={resetTransform}
                  className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  title={t('common.reset')}
                >
                  <Maximize2 className="w-4 h-4 text-white" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Thumbnails */}
        {images.length > 1 && !drawMode && (
          <div className="flex gap-2 px-4 py-2.5 bg-neutral-800 border-t border-white/10 overflow-x-auto flex-shrink-0">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`flex-shrink-0 w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
                  i === currentIndex
                    ? 'border-primary-600 opacity-100'
                    : 'border-transparent opacity-40 hover:opacity-80'
                }`}
              >
                <img
                  src={img.url}
                  alt={`thumb ${i + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Comments panel */}
      {showComments && currentPhotoId && (
        <PhotoCommentsPanel
          photoId={currentPhotoId}
          onClose={() => setShowComments(false)}
          onCountChange={setCommentCount}
        />
      )}
    </div>,
    document.body,
  );
}
